import FileCopyIcon from '@mui/icons-material/FileCopyOutlined';
import { Avatar, Grid, IconButton, TextField, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TimePicker } from '../components/TimePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import * as React from 'react';

const DOW = {
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
};

const DOW3 = {
    0: 'Sun',
    1: 'Mon',
    2: 'Tue',
    3: 'Wed',
    4: 'Thu',
    5: 'Fri',
    6: 'Sat',
};

const useDayStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        '& > *': {
            margin: theme.spacing(1),
        },
    },
    selected: {
        cursor: 'pointer',
    },
    unselected: {
        cursor: 'pointer',
    },
}));

const useRootStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
    },
}));

const Day = (props) => {
    const theme = useTheme();
    const handleClick = (ev) => {
        // console.log('inside day click', props.id, ev);
        ev.stopPropagation();
        props.onClick && props.onClick(parseInt(props.id, 10));
    };
    return (
        <div style={{ marginRight: theme.spacing(1) }} onClick={!props.disabled ? handleClick : () => console.log('write permissions not found')}>
            <Avatar style={{ ...props.style }} className={props.className}>
                {props.label}
            </Avatar>
        </div>
    );
};

const DayList = (props) => {
    const classes = useDayStyles();
    const selected = props.selected || [];
    const theme = useTheme();

    const list = Object.keys(DOW).map((k) => (
        <Day key={k} disabled={props.disabled} className={selected.indexOf(parseInt(k, 10)) >= 0 ? classes.selected : classes.unselected} id={k} label={DOW[k].charAt(0)} onClick={props.onClick} style={{ backgroundColor: selected.indexOf(parseInt(k, 10)) >= 0 && theme.palette.primary.main }} />
    ));
    return <React.Fragment>{list}</React.Fragment>;
};

const DayHours = (props) => {
    const genHandleTimeChange = (field, day) => (time) => {
        props.onChange && props.onChange(field, day, time);
    };

    // console.log('DayHours', props.day, props.from, props.to);

    return (
        <Grid item xs={12} md={6} container spacing={props.isSmallScreen ? 1 : 0} direction="row" align="center" justifyContent="center" alignItems="center">
            <Grid item xs={props.isSmallScreen ? 2 : 4} md={2} align="start">
                {props.isSmallScreen ? DOW3[props.day] : DOW[props.day]}
            </Grid>
            <Grid item xs={props.isSmallScreen ? 9 : 7} sm={8} container direction="row" spacing={0}>
                <Grid item xs={6}>
                    <TimePicker disabled={props.disabled} renderInput={(props) => <TextField  {...props} />} showTodayButton todayLabel="now" label="From" inputVariant="filled" size="small" value={props.from} onChange={genHandleTimeChange('from', props.day)} />
                </Grid>
                <Grid item xs={6}>
                    <TimePicker disabled={props.disabled} renderInput={(props) => <TextField {...props} />} showTodayButton todayLabel="now" label="To" inputVariant="filled" size="small" value={props.to} onChange={genHandleTimeChange('to', props.day)} />
                </Grid>
            </Grid>
            <Grid item xs={1}>
                {props.isFirst ? (
                    <IconButton disabled={props.disabled} color="primary" size="small" style={{ whiteSpace: 'nowrap' }} onClick={props.onCopyTimeClick}>
                        <FileCopyIcon />
                        &nbsp;{!props.isSmallScreen ? 'Copy Time to All' : null}
                    </IconButton>
                ) : null}
            </Grid>
        </Grid>
    );
};

const _time2moment = (tm) => {
    const m = moment().utcOffset(0);
    if (typeof tm.hours !== void 0) {
        m.hours(tm.hours);
    }
    if (typeof tm.minutes !== void 0) {
        m.minutes(tm.minutes);
    }
    // console.log('_time2moment', tm.hours, tm.minutes, '->', m.format());
    return m;
};

const fromGoogleOpeningHours = (oh) => {
    const res = {};

    if (oh && oh.length === 1 && !!oh[0].open && !oh[0].close && oh[0].open.day === 0 && oh[0].open.hours === 0 && oh[0].open.minutes === 0) {
        // special case covered in Google Places API docs:
        // https://developers.google.com/maps/documentation/places/web-service/details#PlaceDetailsResults
        //
        // ```If a place is always open, the close section will be missing from the response.
        // Clients can rely on always-open being represented as an open period containing day
        // with value 0 and time with value 0000, and no close.```
        Array.from(Array(7).keys()).forEach((dayIdx) => {
            res[dayIdx] = {
                hide: false,
                day: dayIdx,
                from: _time2moment({ hours: 0, minutes: 0 }),
                to: _time2moment({ hours: 23, minutes: 59 }),
            };
        });
    } else if (oh && oh.forEach) {
        oh.forEach((period) => {
            if (period.close && period.close.day !== period.open.day) {
                const startDate = _time2moment(period.open);
                for (let i = period.open.day; i <= period.close.day; i++) {
                    const rec = { day: i, hide: !!period.hide };
                    if (i === period.open.day) {
                        rec.from = _time2moment(period.open);
                        rec.to = _time2moment(period.open).endOf('day').valueOf(); // valueOf produces unix timestamp in milliseconds in MomentJS
                    } else if (i === period.close.day) {
                        rec.from = _time2moment(period.close).startOf('day').valueOf();
                        rec.to = _time2moment(period.close);
                    } else {
                        const iDay = startDate.clone().add(i - period.open.day + 1, 'day');
                        rec.from = iDay.clone().startOf('day').valueOf();
                        rec.to = iDay.clone().endOf('day').valueOf();
                    }
                    res[rec.day] = rec;
                }
            } else {
                res[period.open.day] = {
                    hide: !!period.hide,
                    day: period.open.day,
                    from: _time2moment(period.open),
                    to: period.close ? _time2moment(period.close) : null,
                };
            }
        });
    }
    return res;
};

const toGoogleOpeningHours = (days) => {
    return Object.keys(days).map((day) => {
        return {
            hide: !!days[day].hide,
            open: {
                day: +day,
                time: days[day].from.format('HHmm'),
                hours: days[day].from.hour(),
                minutes: days[day].from.minute(),
            },
            close: {
                day: +day,
                time: days[day].to.format('HHmm'),
                hours: days[day].to.hour(),
                minutes: days[day].to.minute(),
            },
        };
    });
};

const _getNewDay = (day) => ({
    hide: false,
    day: +day,
    from: _time2moment({ hours: 9, minutes: 0 }),
    to: _time2moment({ hours: 17, minutes: 0 }),
});

const HoursOfOperation = (props) => {
    const classes = useRootStyles();
    const days = fromGoogleOpeningHours(props.schedule);
    const handleDayClick = (dayIdx) => {
        // console.log('day click', dayIdx, days[dayIdx]);
        if (!days[dayIdx]) {
            days[dayIdx] = _getNewDay(dayIdx);
        } else if (days[dayIdx].hide) {
            days[dayIdx].hide = false;
        } else {
            days[dayIdx].hide = true;
        }
        // console.log('day click', dayIdx, days[dayIdx]);
        props.onChange(toGoogleOpeningHours(days));
    };
    const handleTimeChange = (field, day, time) => {
        if (!days[day]) {
            days[day] = _getNewDay(day);
            console.warning('handleTimeChange: time changed for a non-existing day', day, 'This is weird.');
        }
        days[day][field] = time;
        const schedule = toGoogleOpeningHours(days);

        // console.log('time change', field, day, time.format(), schedule);
        props.onChange(schedule);
    };
    const selected = Object.keys(days)
        .filter((dayIdx) => !days[dayIdx].hide)
        .map((v) => +v);
    const handleCopyTime = (ev) => {
        const dayIdx = selected[0];
        const srcDay = days[dayIdx];
        // console.log('copying time of the first day', dayIdx, 'to all other days');
        Object.keys(days)
            .filter((day) => !days[day].hide)
            .forEach((day) => {
                days[day].from = srcDay.from;
                days[day].to = srcDay.to;
            });
        props.onChange(toGoogleOpeningHours(days));
    };
    // console.log('render', props.schedule, days, selected);
    const dayHoursList = selected.map((dayIdx, arrIdx) => <DayHours key={+dayIdx} day={+dayIdx} from={moment(days[dayIdx].from).utc()} to={moment(days[dayIdx].to).utc()} isFirst={arrIdx === 0} onChange={handleTimeChange} onCopyTimeClick={handleCopyTime} isSmallScreen={props.isSmallScreen} />);
    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Grid container direction="column" spacing={2} className={classes.root}>
                <Grid item xs={12} container spacing={0}>
                    <DayList disabled={props.disabled} selected={selected} onClick={handleDayClick} isSmallScreen={props.isSmallScreen} />
                </Grid>
                {dayHoursList}
            </Grid>
        </LocalizationProvider>
    );
};

export default HoursOfOperation;
